import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type {
  Appointment,
  CarByEffortItem,
  CarsByEffortPeriodRow,
  CarsThroughputPeriodGroupBy,
  ProgressStep,
} from "../../../types";
import { adminApi, appointmentsApi } from "../../../services";
import { formatDateForDisplay, formatTime } from "../../../utils/dateUtils";
import {
  formatMinutesDuration,
  formatThroughputPeriodLabel,
  getPeriodRequestBounds,
  EFFORT_ORDER,
} from "../../../utils/carsThroughputUtils";
import {
  filterProgressStepsByProfessional,
  formatProgressDuration,
  getProgressStepDurationMs,
  getProgressStepStatus,
  sumProgressStepMinutes,
} from "../../../utils/appointmentProgressUtils";
import { AdminPageHeaderWithBack } from "../shared/AdminPageHeaderWithBack";
import { Avatar, Card, CarBrandLogo, ErrorState, Icons, Loading } from "../../shared";

const GROUP_BY_LABELS: Record<CarsThroughputPeriodGroupBy, string> = {
  day: "dia",
  week: "semana",
  month: "mês",
};

function sortedSteps(steps: ProgressStep[]): ProgressStep[] {
  if (!steps.length) return [];
  return [...steps].sort((a, b) => a.id - b.id);
}

function formatStepClock(iso: string | null): string {
  if (!iso) return "—";
  return formatTime(iso);
}

const stepStatusBadge: Record<string, string> = {
  completed: "bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-800",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800",
  pending: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600",
};

const stepStatusLabel: Record<string, string> = {
  completed: "Concluída",
  in_progress: "Em andamento",
  pending: "Pendente",
};

function CarAppointmentAccordion({
  car,
  professionalId,
}: {
  car: CarByEffortItem;
  professionalId?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!expanded) return;

    const hasActiveStep = (appointment?.progressSteps ?? []).some(
      (step) => step.startTime && !step.endTime,
    );
    if (!hasActiveStep) return;

    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [expanded, appointment?.progressSteps]);

  const loadAppointment = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await appointmentsApi.getAppointmentById(car.appointmentId);
      if (res.success && res.data) {
        setAppointment(res.data);
      } else {
        setAppointment(null);
        setError(res.error || "Erro ao carregar atendimento");
      }
    } catch (e) {
      setAppointment(null);
      setError(
        e instanceof Error ? e.message : "Erro ao carregar atendimento",
      );
    } finally {
      setLoading(false);
    }
  }, [car.appointmentId]);

  const handleToggle = () => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && !appointment && !loading) {
      void loadAppointment();
    }
  };

  const steps = useMemo(
    () =>
      sortedSteps(
        filterProgressStepsByProfessional(
          appointment?.progressSteps,
          professionalId,
        ),
      ),
    [appointment?.progressSteps, professionalId],
  );

  const totalMinutes = useMemo(
    () => (appointment ? sumProgressStepMinutes(steps, now) : car.totalProgressMinutes),
    [appointment, steps, now, car.totalProgressMinutes],
  );

  return (
    <li>
      <button
        type="button"
        onClick={handleToggle}
        className="w-full px-3 py-3 sm:px-4 text-left hover:bg-sky-50 dark:hover:bg-slate-700/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400"
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
              <CarBrandLogo brand={car.clientCar.brand} />
              <span>
                {car.clientCar.plate} · {car.clientCar.model}
              </span>
            </p>
            {car.serviceName ? (
              <p className="text-sm text-gray-700 dark:text-slate-300 mt-0.5 truncate" title={car.serviceName}>
                {car.serviceName}
              </p>
            ) : null}
            <Link
              to={`/admin/clients/${car.client.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-sky-700 dark:text-sky-400 hover:underline"
            >
              {car.client.name}
            </Link>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
              Atendimento em {formatDateForDisplay(car.appointmentDate)}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              className="flex items-center gap-2 rounded-lg bg-sky-50 dark:bg-sky-950/50 border border-sky-200 dark:border-sky-800 px-2.5 py-1.5 sm:px-3 sm:py-2"
              title="Tempo total nas etapas"
            >
              <Icons.Schedule sx={{ fontSize: 18 }} className="text-sky-700 dark:text-sky-400" />
              <div className="text-right">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-400 leading-none">
                  Tempo
                </p>
                <p className="text-sm sm:text-base font-bold text-sky-950 dark:text-sky-100 tabular-nums leading-tight mt-0.5">
                  {formatMinutesDuration(totalMinutes)}
                </p>
              </div>
            </div>
            <ExpandMore
              sx={{
                fontSize: 24,
                color: "#6b7280",
                flexShrink: 0,
                transition: "transform 0.2s",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-4 sm:px-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40">
          <div className="flex justify-end pt-2 pb-1">
            <Link
              to={`/admin/appointments/${car.appointmentId}`}
              className="text-xs font-medium text-sky-700 dark:text-sky-400 hover:underline"
            >
              Ver agendamento completo
            </Link>
          </div>

          {loading && (
            <Loading message="Carregando etapas…" size="sm" className="py-4" />
          )}

          {!loading && error && (
            <ErrorState
              message={error}
              variant="inline"
              onRetry={loadAppointment}
              className="py-2"
            />
          )}

          {!loading && !error && steps.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-slate-400 py-2">
              Nenhuma etapa registrada neste atendimento.
            </p>
          )}

          {!loading && !error && steps.length > 0 && (
            <>
              <ul className="space-y-2 pt-1">
                {steps.map((step) => {
                  const status = getProgressStepStatus(step);
                  const durationMs = getProgressStepDurationMs(step, now);
                  const name =
                    step.serviceSubService?.subService?.name || "Etapa";

                  return (
                    <li
                      key={step.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${stepStatusBadge[status]}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {step.professionals && step.professionals.length > 0 && (
                            <div
                              className="flex items-center -space-x-2 shrink-0"
                              title={step.professionals.map((p) => p.name).join(", ")}
                            >
                              {step.professionals.map((prof) => (
                                <Avatar
                                  key={prof.id}
                                  text={prof.name}
                                  src={prof.picture}
                                  size="xs"
                                  variant="blue"
                                  className="ring-2 ring-white"
                                />
                              ))}
                            </div>
                          )}
                          <span className="font-medium text-gray-900 dark:text-inherit truncate">
                            {name}
                          </span>
                          <span className="text-xs opacity-80 shrink-0">
                            ({stepStatusLabel[status]})
                          </span>
                        </div>
                        {step.professionals && step.professionals.length > 0 && (
                          <p className="text-xs mt-0.5 opacity-90 truncate pl-0.5">
                            {step.professionals.map((pr) => pr.name).join(", ")}
                          </p>
                        )}
                        {step.notes?.trim() && (
                          <p className="text-xs mt-1 text-gray-700 pl-0.5 whitespace-pre-wrap">
                            {step.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm shrink-0">
                        <span>
                          {formatStepClock(step.startTime)} →{" "}
                          {formatStepClock(step.endTime)}
                        </span>
                        {status !== "pending" && (
                          <span
                            className={`font-mono font-semibold ${
                              status === "in_progress" ? "animate-pulse" : ""
                            }`}
                          >
                            {formatProgressDuration(durationMs)}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 flex items-center justify-end gap-2 text-sm font-medium text-gray-600 dark:text-slate-300">
                <span>Total das etapas:</span>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 dark:bg-sky-950/50 border border-sky-200 dark:border-sky-800 px-3 py-1.5 font-bold text-sky-950 dark:text-sky-100 tabular-nums">
                  <Icons.Schedule sx={{ fontSize: 16 }} className="text-sky-700 dark:text-sky-400" />
                  {formatMinutesDuration(totalMinutes)}
                </span>
              </p>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export const CarsThroughputPeriodDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const groupBy = (searchParams.get("groupBy") || "day") as CarsThroughputPeriodGroupBy;
  const period = searchParams.get("period") || "";
  const professionalIdParam = searchParams.get("professionalId");
  const professionalId = professionalIdParam
    ? parseInt(professionalIdParam, 10)
    : undefined;
  const professionalIdForApi = Number.isFinite(professionalId)
    ? professionalId
    : undefined;

  const periodBounds = useMemo(
    () => (period ? getPeriodRequestBounds(groupBy, period) : null),
    [groupBy, period],
  );

  const [periodData, setPeriodData] = useState<CarsByEffortPeriodRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEfforts, setExpandedEfforts] = useState<Set<string>>(() => new Set());

  const loadPeriodData = useCallback(async () => {
    if (!periodBounds || !period) {
      setError("Período inválido.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await adminApi.getCarsByEffort({
        startDate: periodBounds.startDate,
        endDate: periodBounds.endDate,
        groupBy,
        professionalId: professionalIdForApi,
      });

      if (res.success && res.data) {
        const match =
          res.data.periods.find((row) => row.period === period) ??
          res.data.periods[0] ??
          null;
        setPeriodData(match);
      } else {
        setPeriodData(null);
        setError(res.error || "Erro ao carregar detalhes do período");
      }
    } catch (e) {
      setPeriodData(null);
      setError(
        e instanceof Error ? e.message : "Erro ao carregar detalhes do período",
      );
    } finally {
      setLoading(false);
    }
  }, [groupBy, period, periodBounds, professionalIdForApi]);

  useEffect(() => {
    loadPeriodData();
  }, [loadPeriodData]);

  useEffect(() => {
    if (!periodData) return;

    const firstEffortWithCars = EFFORT_ORDER.find(
      ({ key }) => (periodData.byEffort[key]?.cars.length ?? 0) > 0,
    );
    if (firstEffortWithCars) {
      setExpandedEfforts(new Set([firstEffortWithCars.key]));
    }
  }, [periodData]);

  const toggleEffort = (key: string) => {
    setExpandedEfforts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const periodLabel = period
    ? formatThroughputPeriodLabel(period, groupBy)
    : "Período";

  const totalCars = EFFORT_ORDER.reduce((sum, { key }) => {
    return sum + (periodData?.byEffort[key]?.cars.length ?? 0);
  }, 0);

  const backQuery = new URLSearchParams();
  if (professionalIdParam) backQuery.set("professionalId", professionalIdParam);
  const backUrl =
    backQuery.toString().length > 0
      ? `/admin/cars-throughput?${backQuery.toString()}`
      : "/admin/cars-throughput";

  if (!period || !periodBounds) {
    return (
      <div className="space-y-4 animate-fade-in">
        <AdminPageHeaderWithBack
          icon={<Icons.Car sx={{ fontSize: 28 }} />}
          title="Detalhes do período"
          subtitle="Período não informado."
          onBackClick={() => navigate(backUrl)}
        />
        <Card className="p-6">
          <ErrorState message="Link inválido. Volte ao relatório e selecione um período." />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeaderWithBack
        icon={<Icons.Car sx={{ fontSize: 28 }} />}
        title={periodLabel}
        subtitle={`Veículos concluídos por esforço · agrupamento por ${GROUP_BY_LABELS[groupBy]}`}
        onBackClick={() => navigate(backUrl)}
      />

      {loading && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <Loading message="Carregando veículos do período…" size="md" className="py-8" />
        </Card>
      )}

      {!loading && error && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <ErrorState message={error} variant="inline" onRetry={loadPeriodData} />
        </Card>
      )}

      {!loading && !error && !periodData && (
        <Card className="p-10 border border-gray-100 shadow-sm text-center">
          <Icons.Car sx={{ fontSize: 48 }} className="text-gray-300 mx-auto" />
          <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
            Nenhum veículo neste período
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
            Não há atendimentos concluídos com veículo em {periodLabel}.
          </p>
        </Card>
      )}

      {!loading && !error && periodData && (
        <>
          <Card className="p-4 sm:p-6 border border-gray-100 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 px-4 py-3">
                <Icons.Car sx={{ fontSize: 24 }} className="text-slate-600 dark:text-slate-300" />
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Total no período</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums">
                    {totalCars}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                {formatDateForDisplay(periodBounds.startDate)}
                {periodBounds.startDate !== periodBounds.endDate
                  ? ` — ${formatDateForDisplay(periodBounds.endDate)}`
                  : null}
              </p>
            </div>
          </Card>

          <div className="space-y-4">
            {EFFORT_ORDER.map(({ key, label, bar, text }) => {
              const cars = periodData.byEffort[key]?.cars ?? [];
              if (cars.length === 0) return null;

              const isEffortExpanded = expandedEfforts.has(key);

              return (
                <Card
                  key={key}
                  className="p-4 sm:p-6 border border-gray-100 shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => toggleEffort(key)}
                    className="w-full flex items-center justify-between gap-3 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50 -mx-1 px-1 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                    aria-expanded={isEffortExpanded}
                  >
                    <div className={`border-l-4 pl-3 ${bar}`}>
                      <h2 className={`text-lg font-semibold ${text}`}>
                        Esforço {label}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                        {cars.length}{" "}
                        {cars.length === 1 ? "veículo" : "veículos"}
                      </p>
                    </div>
                    <ExpandMore
                      sx={{
                        fontSize: 28,
                        color: "#6b7280",
                        flexShrink: 0,
                        transition: "transform 0.2s",
                        transform: isEffortExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </button>

                  {isEffortExpanded && (
                    <ul className="mt-4 divide-y divide-gray-100 dark:divide-slate-700 rounded-xl border border-gray-200 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-800">
                      {cars.map((car) => (
                        <CarAppointmentAccordion
                          key={car.appointmentId}
                          car={car}
                          professionalId={professionalIdForApi}
                        />
                      ))}
                    </ul>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
